import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import {
  Box, Button, Checkbox, PageHeader, Pagination, TableSkeleton, generateTableFromSchema, useTable,
} from '@zolteam/zol-admin-ui';

// Services
import { useGetAdminUsersListQuery } from '../../services/parazol/parazolQueries';

const UserListSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Utilisateur',
  properties: {
    firstName: {
      type: 'string',
      description: 'Prénom',
      x_layout: '1/1/1/7',
    },
    lastName: {
      type: 'string',
      description: 'Nom',
      x_layout: '1/7/1/13',
    },
    emailAddress: {
      type: 'string',
      format: 'email',
      description: 'Email',
      x_layout: '2/1/2/13',
    },
    hasEmailAddressVerified: {
      type: 'boolean',
      description: 'Email verifié',
      x_layout: '3/1/3/13',
    },
    isActive: {
      type: 'boolean',
      description: 'Utilisateur actif',
      x_layout: '4/1/4/13',
    },
  },
  additionalProperties: false,
};

/**
 * UserList component.
 * @returns {JSX.Element}
 */
function UserList() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || 1;
  const pageSize = searchParam.get('pageSize') || 10;
  const sort = searchParam.get('sort') || null;
  const order = searchParam.get('order') || null;

  const {
    handlePaginationChange,
    handleColumnSort,
    countSelectedRows,
    isRowSelected,
    selectedRows,
    dispatch: useTableDispatch,
  } = useTable({
    setParams: (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
  });

  // Services
  const {
    data: dataUsersList,
    isLoading: isLoadingDataUserList,
  } = useGetAdminUsersListQuery(
    { pageNumber: Number(pageNumber), pageSize: Number(pageSize) },
    { keepPreviousData: true },
  );

  // States
  const paginationCountLabel = (
    currentPageMin,
    currentPageMax,
    count,
  ) => t('cortizol.pagination.resultByPage', { currentPageMin, currentPageMax, count });

  const pageRowsAreSelected = useMemo(() => {
    if (!dataUsersList?.data?.length) return false;
    return dataUsersList.data.every((row) => isRowSelected(row));
  }, [dataUsersList?.data, isRowSelected]);

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('/users/new');
  /**
   * Handle the click on a table row.
   * @param {any} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => navigate(`/user/${row.id}/profile`);

  return (
    <Box direction="column" gap="s" className="padding-l">
      <PageHeader
        title={(
          <>
            <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
              {t('parazol.userList.title')}
            </h1>
            {dataUsersList?.data.length && dataUsersList?.pagination.recordsCount ? (
              <span className="font-color-neutral-500">
                {t('parazol.userList.nbRecords', { count: dataUsersList.pagination.recordsCount })}
              </span>
            ) : null}
          </>
        )}
        actions={(
          <Button icon="add" onClick={handleNewContentClick}>
            {t('parazol.userList.createNewUser')}
          </Button>
        )}
      />

      {countSelectedRows ? (
        <Box align="center" justify="flex-end" fullWidth>
          {t('parazol.userList.nbRecordsSelected', { count: countSelectedRows })}
          <Button color="noBackground" size="small" onClick={() => useTableDispatch({ type: 'RESET_SELECTED_ROWS' })}>{t('parazol.userList.reset')}</Button>
        </Box>
      ) : null}

      {isLoadingDataUserList
        ? <TableSkeleton />
        : generateTableFromSchema(
          {
            JSONSchema: UserListSchema,
            data: dataUsersList.data,
            sort: {
              order,
              active: sort,
              handleColumnSort,
            },
            navigate: {
              handleRowNavigate,
            },
            labelNoResult: t('parazol.userList.noResult'),
            customColumns: [
              {
                key: 'checkbox',
                description: '',
                sortable: false,
                position: 'start',
                renderHead: () => (
                  <Box>
                    <Checkbox
                      id="all"
                      checked={pageRowsAreSelected}
                      onChange={() => useTableDispatch({ type: 'SELECT_ALL_ROWS', payload: dataUsersList.data })}
                    />
                  </Box>
                ),
                renderCell: (/** @type {AdminUser} */row) => {
                  const isChecked = !!selectedRows?.[row.id] || false;
                  return (
                    <Box className="margin-s">
                      <Checkbox
                        id={row.id}
                        checked={isChecked}
                        onChange={() => useTableDispatch({ type: isChecked ? 'DESELECT_ROW' : 'SELECT_ROW', payload: row })}
                      />
                    </Box>
                  );
                },
              },
            ],
          },
        )}

      {dataUsersList?.data.length && dataUsersList?.pagination.recordsCount ? (
        <Pagination
          recordsCount={dataUsersList.pagination.recordsCount}
          pageNumber={+pageNumber}
          pageSize={+pageSize}
          onPageNumberChange={handlePaginationChange}
          countLabelCallback={paginationCountLabel}
          perPageResultLabel={t('cortizol.pagination.resultPerPage')}
        />
      ) : null}

    </Box>
  );
}

export default UserList;
